/**
 * Klaviyo API Helper Functions
 * These functions interact with Klaviyo's Client API
 */

/**
 * Identify/Update a Klaviyo profile with properties like first_name
 * @param {Object} formData - Form data containing email, name, etc.
 * @param {string} companyId - Klaviyo company ID
 * @returns {Promise<Object>} Response from Klaviyo API
 */
async function identifyKlaviyoProfile(formData, companyId) {
  const apiUrl = `https://a.klaviyo.com/client/profiles/?company_id=${companyId}`;

  const profileData = {
    email: formData.email,
  };

  if (formData.name || formData.first_name) {
    profileData.first_name = formData.first_name || formData.name;
  }

  if (formData.last_name) {
    profileData.last_name = formData.last_name;
  }

  if (formData.phone_number) {
    profileData.phone_number = formData.phone_number;
  }

  const payload = {
    data: {
      type: "profile",
      attributes: profileData,
    },
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-10-15",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: "Failed to parse error response" };
      }
      console.error("Klaviyo Profile API Error:", errorData);
      throw new Error(`Profile API failed: ${response.status}`);
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = { success: true };
    }

    return data;
  } catch (error) {
    console.error("Error identifying Klaviyo profile:", error);
    throw error;
  }
}

/**
 * Submit back-in-stock subscription to Klaviyo
 * @param {Object} formData - Form data containing email
 * @param {string} companyId - Klaviyo company ID
 * @param {string} variantId - Shopify variant ID
 * @param {string} listName - Name of the list (optional)
 * @returns {Promise<Object>} Response from Klaviyo API
 */
async function submitToKlaviyoBackInStock(
  formData,
  companyId,
  variantId,
  listName = "Product Back-in-Stock"
) {
  const apiUrl = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`;

  const formattedVariantId = `$shopify:::$default:::${variantId}`;

  const profileAttributes = {
    email: formData.email,
  };

  if (formData.phone_number) {
    profileAttributes.phone_number = formData.phone_number;
  }

  const payload = {
    data: {
      type: "back-in-stock-subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            attributes: profileAttributes,
          },
        },
        channels: ["EMAIL"],
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: formattedVariantId,
          },
        },
      },
    },
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-10-15",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: "Failed to parse error response" };
      }
      console.error("Klaviyo Back-in-Stock API Error:", errorData);
      throw new Error(`Back-in-Stock API failed: ${response.status}`);
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = { success: true };
    }

    return data;
  } catch (error) {
    console.error("Error submitting to Klaviyo Back-in-Stock:", error);
    throw error;
  }
}

/**
 * Submit newsletter subscription to Klaviyo
 * @param {Object} formData - Form data containing email
 * @param {string} companyId - Klaviyo company ID
 * @param {string} listId - Klaviyo list ID
 * @returns {Promise<Object>} Response from Klaviyo API
 */
async function submitToKlaviyoNewsletter(formData, companyId, listId) {
  const apiUrl = `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`;

  const profileData = {
    email: formData.email,
  };

  if (formData.name || formData.first_name) {
    profileData.first_name = formData.first_name || formData.name;
  }

  if (formData.phone_number) {
    profileData.phone_number = formData.phone_number;
  }

  const payload = {
    data: {
      type: "subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            attributes: profileData,
          },
        },
        custom_source: "Newsletter Signup",
      },
      relationships: {
        list: {
          data: {
            type: "list",
            id: listId,
          },
        },
      },
    },
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-10-15",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: "Failed to parse error response" };
      }
      console.error("Klaviyo Newsletter API Error:", errorData);
      throw new Error(`Newsletter API failed: ${response.status}`);
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = { success: true };
    }

    return data;
  } catch (error) {
    console.error("Error submitting to Klaviyo Newsletter:", error);
    throw error;
  }
}
